import { useCallback, useState } from "react";
import { useAssessmentSession } from "../contexts/assessmentUserContext";
import { getApiUrls } from "../lib/apiUrl";
import {
  createAssessmentBenchmarkRequest,
  deleteAssessmentBenchmarkRequest,
  getAssessmentBenchmarkRequest,
  uploadAssessmentBenchmarkAssetRequest,
  type BenchmarkDetail,
  type BenchmarkImageInput,
  type BenchmarkSummary,
} from "../services/assessmentContentService";

type CreateBenchmarkInput = {
  id?: string;
  title: string;
  description?: string;
  benchmark_type: "boy" | "moy" | "eoy";
  skill: "reading" | "listening" | "speaking" | "writing";
  start_date: string;
  end_date: string;
  district_ids: string[];
  school_ids?: string[];
  grade_ids?: string[];
  image?: BenchmarkImageInput;
  remove_image?: boolean;
};

function toUniqueImageFile(file: File): File {
  return new File([file], `${crypto.randomUUID()}-${file.name}`, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

export function useAssessmentBenchmark() {
  const { token } = useAssessmentSession();
  const { assessmentsApiUrl: apiUrl } = getApiUrls();
  const [isSavingBenchmark, setIsSavingBenchmark] = useState(false);
  const [isDeletingBenchmark, setIsDeletingBenchmark] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const createBenchmark = useCallback(
    async (input: CreateBenchmarkInput): Promise<BenchmarkSummary> => {
      setIsSavingBenchmark(true);
      try {
        return await createAssessmentBenchmarkRequest({
          apiUrl,
          token,
          ...input,
        });
      } finally {
        setIsSavingBenchmark(false);
      }
    },
    [apiUrl, token],
  );

  const uploadBenchmarkImage = useCallback(
    async (benchmarkId: string, file: File): Promise<BenchmarkImageInput> => {
      setIsUploadingImage(true);
      try {
        const uploaded = await uploadAssessmentBenchmarkAssetRequest({
          apiUrl,
          token,
          benchmarkId,
          assetType: "images",
          file: toUniqueImageFile(file),
          isDemo: false,
        });
        return { key: uploaded.key, file_type: file.type };
      } finally {
        setIsUploadingImage(false);
      }
    },
    [apiUrl, token],
  );

  const getBenchmark = useCallback(
    async (id: string): Promise<BenchmarkDetail> =>
      getAssessmentBenchmarkRequest({
        apiUrl,
        token,
        id,
      }),
    [apiUrl, token],
  );

  const deleteBenchmark = useCallback(
    async (id: string): Promise<void> => {
      setIsDeletingBenchmark(true);
      try {
        await deleteAssessmentBenchmarkRequest({
          apiUrl,
          token,
          id,
        });
      } finally {
        setIsDeletingBenchmark(false);
      }
    },
    [apiUrl, token],
  );

  return {
    isSavingBenchmark,
    isDeletingBenchmark,
    isUploadingImage,
    createBenchmark,
    uploadBenchmarkImage,
    getBenchmark,
    deleteBenchmark,
  };
}
